import type { ChatContext } from "@/store/chat-types";
import { useStudioStore } from "@/store/studio-store";
import type { StudioMcpBus } from "@/mcp/studio-mcp-bus";
import {
  listPendingStudioCapabilities,
  listStudioCapabilityExecutions,
  type StudioCapabilityExecution,
  type StudioPendingCapability,
} from "./studio-capability-gateway";
import {
  buildStudioChatOperationContext,
  getStudioChatOperationContract,
} from "./studio-chat-operation-registry";

export const STUDIO_GOLDEN_JOURNEY_SCHEMA =
  "cogo.studio.golden-journey-session.v1" as const;

export type StudioGoldenJourneyId =
  | "semantic-sync-publish"
  | "figma-to-publish"
  | "document-roundtrip"
  | "data-actionflow";

export type StudioGoldenJourneyStatus =
  | "active"
  | "awaiting-confirmation"
  | "completed"
  | "blocked"
  | "failed";

export type StudioGoldenJourneyStepStatus =
  | "pending"
  | "active"
  | "awaiting-confirmation"
  | "completed"
  | "blocked"
  | "failed"
  | "undone";

export interface StudioGoldenJourneyStep {
  id: string;
  label: string;
  operationId: string;
  instruction: string;
  proof: string;
}

export interface StudioGoldenJourneyDefinition {
  id: StudioGoldenJourneyId;
  label: string;
  summary: string;
  startExamples: string[];
  steps: StudioGoldenJourneyStep[];
}

export interface StudioGoldenJourneySession {
  schemaVersion: typeof STUDIO_GOLDEN_JOURNEY_SCHEMA;
  journeyId: StudioGoldenJourneyId;
  projectId: string | null;
  startedAt: string;
  updatedAt: string;
}

export interface StudioGoldenJourneyStepSnapshot extends StudioGoldenJourneyStep {
  status: StudioGoldenJourneyStepStatus;
  executionId?: string;
  confirmationId?: string;
  error?: string;
}

export interface StudioGoldenJourneySnapshot {
  definition: StudioGoldenJourneyDefinition;
  session: StudioGoldenJourneySession;
  status: StudioGoldenJourneyStatus;
  completedStepCount: number;
  totalStepCount: number;
  currentStep: StudioGoldenJourneyStepSnapshot | null;
  steps: StudioGoldenJourneyStepSnapshot[];
  nextPrompt: string;
}

export type StudioGoldenJourneyIntent =
  | { action: "start"; journeyId: StudioGoldenJourneyId }
  | { action: "continue" }
  | { action: "inspect" };

const STORAGE_PREFIX = "cogo.studio.golden-journey.v1.";
const sessions = new Map<string, StudioGoldenJourneySession>();
const listeners = new Set<() => void>();
let revision = 0;

export const STUDIO_GOLDEN_JOURNEYS: Record<
  StudioGoldenJourneyId,
  StudioGoldenJourneyDefinition
> = {
  "semantic-sync-publish": {
    id: "semantic-sync-publish",
    label: "Change once, sync everything",
    summary:
      'Apply one bounded change set to App, Design, and Guide, verify every exact target in Status, and prepare one publication candidate with a single approval.',
    startExamples: [
      "Start the change once, sync everything journey.",
      "한 번 바꾸고 App, Design, Guide를 동기화한 뒤 발행 후보를 준비해줘.",
    ],
    steps: [
      {
        id: "semantic-sync-approve",
        label: "Approve coordinated change",
        operationId: "workspace.semantic-sync",
        instruction:
          "Resolve the exact App page and Guide node, enter the approved title, primary color, and Guide copy, then review one combined impact preview.",
        proof:
          "Exact App, Design, and Guide checks plus a locked publication-candidate receipt; production deployment remains a separate authority.",
      },
    ],
  },
  "figma-to-publish": {
    id: "figma-to-publish",
    label: "Figma to publish",
    summary:
      "Inspect an imported Figma source, accept bounded mappings, review visual changes, prove the candidate, and prepare publishing.",
    startExamples: [
      "Start the Figma to publish journey.",
      "Figma 파일부터 배포 준비까지 진행해줘.",
    ],
    steps: [
      {
        id: "figma-inspect",
        label: "Inspect source",
        operationId: "source.figma.inspect",
        instruction:
          "Inspect what was read from the design and name anything missing.",
        proof: "Design source workspace check",
      },
      {
        id: "figma-map",
        label: "Map pages",
        operationId: "source.figma.apply_mapping",
        instruction:
          "Review and confirm page, story, and screen-role mappings.",
        proof: "Accepted source mapping revision",
      },
      {
        id: "figma-visual-review",
        label: "Review visuals",
        operationId: "source.figma.visual_review",
        instruction:
          "Request bounded visual judgement for every selected screen.",
        proof: "Human or Gemma judgement with evidence refs",
      },
      {
        id: "figma-candidate",
        label: "Create candidate",
        operationId: "source.figma.create_candidate",
        instruction:
          "Create the selected runtime candidate only after source admission passes.",
        proof: "Candidate binding tied to the source revision",
      },
      {
        id: "figma-runtime",
        label: "Prove runtime",
        operationId: "source.figma.runtime_proof",
        instruction:
          "Run the preview scenarios and retain runtime evidence before publishing.",
        proof: "Passing runtime scenario proof",
      },
      {
        id: "figma-publish",
        label: "Prepare publish",
        operationId: "project.publish.confirm",
        instruction:
          "Review the exact external impact and type PUBLISH only when every gate passes.",
        proof: "Confirmed publish readiness and live check",
      },
    ],
  },
  "document-roundtrip": {
    id: "document-roundtrip",
    label: "Document roundtrip",
    summary:
      "Inspect a document page, collaborate through WordGard, approve a semantic proposal, export, and verify the exported file.",
    startExamples: [
      "Start the document roundtrip journey.",
      "문서를 페이지별로 편집하고 DOCX 왕복 검증해줘.",
    ],
    steps: [
      {
        id: "document-inspect",
        label: "Inspect page",
        operationId: "document.page.inspect",
        instruction:
          "Inspect the selected page and its canonical document node before editing.",
        proof: "Page and node check",
      },
      {
        id: "document-draft",
        label: "Open draft",
        operationId: "document.page.wordgard",
        instruction:
          "Open the DOCX or HWPX narrative projection in the WordGard adapter.",
        proof: "Bounded WordGard draft projection",
      },
      {
        id: "document-propose",
        label: "Propose edit",
        operationId: "document.page.propose_edit",
        instruction:
          "Prepare a semantic proposal without mutating the canonical document.",
        proof: "Isolated proposal at the current base revision",
      },
      {
        id: "document-approve",
        label: "Approve proposal",
        operationId: "document.proposal.apply",
        instruction:
          "Review and confirm the proposal before applying the document operation.",
        proof: "Applied operation and incremented revision",
      },
      {
        id: "document-export",
        label: "Export page",
        operationId: "document.page.export",
        instruction:
          "Request the selected native export for the current document revision.",
        proof: "Revision-scoped export request",
      },
      {
        id: "document-readback",
        label: "Verify the export",
        operationId: "document.target.readback",
        instruction:
          "Verify proof references and blockers before claiming the export is ready.",
        proof: "Exported file check",
      },
    ],
  },
  "data-actionflow": {
    id: "data-actionflow",
    label: "Data and ActionFlow",
    summary:
      "Scaffold a source, bind it to a component, connect an event to ActionFlow, and verify the runtime trace.",
    startExamples: [
      "Start the data and ActionFlow journey.",
      "데이터 소스와 액션플로우를 채팅으로 연결해줘.",
    ],
    steps: [
      {
        id: "data-review",
        label: "Review data",
        operationId: "data.surface.review",
        instruction:
          "Inspect the active block, current sources, and unresolved credentials.",
        proof: "Datasource and state check",
      },
      {
        id: "data-scaffold",
        label: "Scaffold source",
        operationId: "data.source.scaffold-from-chat",
        instruction:
          "Create a source scaffold while keeping unresolved endpoint input explicit.",
        proof: "Datasource marked ready or needs-configuration",
      },
      {
        id: "data-bind",
        label: "Bind component",
        operationId: "data.binding.connect",
        instruction:
          "Bind the selected element to the verified source expression.",
        proof: "Component binding check",
      },
      {
        id: "flow-connect",
        label: "Connect event",
        operationId: "behavior.flow.connect-selected",
        instruction:
          "Connect the selected element event to the selected ActionFlow.",
        proof: "Event binding and flow ownership check",
      },
      {
        id: "flow-test",
        label: "Test flow",
        operationId: "behavior.flow.test",
        instruction:
          "Run validation and retain the ActionFlow state transition trace.",
        proof: "Passing validation and runtime trace",
      },
    ],
  },
};

function storage(): Storage | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

function projectId(): string | null {
  return useStudioStore.getState().currentProjectId;
}

function storageKey(id = projectId()): string {
  return `${STORAGE_PREFIX}${encodeURIComponent(id ?? "no-project")}`;
}

function isJourneyId(value: unknown): value is StudioGoldenJourneyId {
  return typeof value === "string" && value in STUDIO_GOLDEN_JOURNEYS;
}

function readSession(): StudioGoldenJourneySession | null {
  const key = storageKey();
  try {
    const raw = storage()?.getItem(key);
    if (!raw) return sessions.get(key) ?? null;
    const parsed = JSON.parse(raw) as Partial<StudioGoldenJourneySession>;
    if (
      parsed.schemaVersion !== STUDIO_GOLDEN_JOURNEY_SCHEMA ||
      !isJourneyId(parsed.journeyId) ||
      parsed.projectId !== projectId() ||
      typeof parsed.startedAt !== "string" ||
      typeof parsed.updatedAt !== "string"
    ) {
      return sessions.get(key) ?? null;
    }
    const session = parsed as StudioGoldenJourneySession;
    sessions.set(key, session);
    return session;
  } catch {
    return sessions.get(key) ?? null;
  }
}

function writeSession(session: StudioGoldenJourneySession | null): void {
  const key = storageKey(session?.projectId);
  if (session) sessions.set(key, session);
  else sessions.delete(key);
  try {
    if (session) storage()?.setItem(key, JSON.stringify(session));
    else storage()?.removeItem(key);
  } catch {
    // Session state remains optional when storage is unavailable.
  }
  revision += 1;
  for (const listener of listeners) listener();
}

function matchesPlan(planId: string, operationId: string): boolean {
  return planId.startsWith(`operation:${operationId}:`);
}

function latestExecution(
  executions: StudioCapabilityExecution[],
  operationId: string,
  startedAt: string,
): StudioCapabilityExecution | undefined {
  const started = Date.parse(startedAt);
  return [...executions]
    .reverse()
    .find(
      (execution) =>
        matchesPlan(execution.planId, operationId) &&
        Date.parse(execution.createdAt) >= started,
    );
}

function latestPending(
  pending: StudioPendingCapability[],
  operationId: string,
  startedAt: string,
): StudioPendingCapability | undefined {
  const started = Date.parse(startedAt);
  return [...pending]
    .reverse()
    .find(
      (entry) =>
        matchesPlan(entry.planId, operationId) &&
        Date.parse(entry.createdAt) >= started,
    );
}

export function subscribeStudioGoldenJourney(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getStudioGoldenJourneyRevision(): number {
  return revision;
}

export function startStudioGoldenJourney(
  journeyId: StudioGoldenJourneyId,
  now = new Date().toISOString(),
): StudioGoldenJourneySession {
  const session: StudioGoldenJourneySession = {
    schemaVersion: STUDIO_GOLDEN_JOURNEY_SCHEMA,
    journeyId,
    projectId: projectId(),
    startedAt: now,
    updatedAt: now,
  };
  writeSession(session);
  return session;
}

export function clearStudioGoldenJourney(): void {
  writeSession(null);
}

export function buildStudioGoldenJourneySnapshot(input: {
  session: StudioGoldenJourneySession;
  pending: StudioPendingCapability[];
  executions: StudioCapabilityExecution[];
}): StudioGoldenJourneySnapshot {
  const definition = STUDIO_GOLDEN_JOURNEYS[input.session.journeyId];
  let firstIncompleteFound = false;
  const steps = definition.steps.map((step) => {
    const execution = latestExecution(
      input.executions,
      step.operationId,
      input.session.startedAt,
    );
    const pending = latestPending(
      input.pending,
      step.operationId,
      input.session.startedAt,
    );
    let status: StudioGoldenJourneyStepStatus = "pending";
    if (pending) status = "awaiting-confirmation";
    else if (execution?.status === "executed") status = "completed";
    else if (execution?.status === "failed") status = "failed";
    else if (execution?.status === "undone") status = "undone";
    else if (!firstIncompleteFound) status = "active";
    if (status !== "completed") firstIncompleteFound = true;
    return {
      ...step,
      status,
      ...(execution ? { executionId: execution.executionId } : {}),
      ...(pending ? { confirmationId: pending.confirmationId } : {}),
      ...(execution?.error ? { error: execution.error } : {}),
    };
  });
  const currentStep = steps.find((step) => step.status !== "completed") ?? null;
  const completedStepCount = steps.filter(
    (step) => step.status === "completed",
  ).length;
  const failed = steps.some((step) => step.status === "failed");
  const awaitingConfirmation = steps.some(
    (step) => step.status === "awaiting-confirmation",
  );
  const status: StudioGoldenJourneyStatus = failed
    ? "failed"
    : completedStepCount === steps.length
      ? "completed"
      : awaitingConfirmation
        ? "awaiting-confirmation"
        : "active";
  return {
    definition,
    session: input.session,
    status,
    completedStepCount,
    totalStepCount: steps.length,
    currentStep,
    steps,
    nextPrompt: currentStep
      ? `${currentStep.instruction} Required proof: ${currentStep.proof}.`
      : `${definition.label} is complete. Review the retained evidence before release.`,
  };
}

export function getActiveStudioGoldenJourneySnapshot(input: {
  pending: StudioPendingCapability[];
  executions: StudioCapabilityExecution[];
}): StudioGoldenJourneySnapshot | null {
  const session = readSession();
  return session
    ? buildStudioGoldenJourneySnapshot({ session, ...input })
    : null;
}

export function activateStudioGoldenJourneyStep(
  snapshot: StudioGoldenJourneySnapshot,
): ChatContext | null {
  const step = snapshot.currentStep;
  if (!step) return null;
  const store = useStudioStore.getState();
  const operation = getStudioChatOperationContract(step.operationId);
  if (!operation) return null;
  const context = {
    ...store.chatContext,
    projectId: store.currentProjectId ?? undefined,
    operationId: operation.id,
    operationLabel: operation.label,
    operationKind: operation.kind,
    operationRequestedAt: Date.now(),
    ...buildStudioChatOperationContext(operation.id, store.chatContext),
    mode: "chat" as const,
    ontologyContext: [
      store.chatContext.ontologyContext,
      `Golden journey: ${snapshot.definition.label}`,
      `Current step: ${step.label}`,
      step.instruction,
      `Required proof: ${step.proof}`,
    ]
      .filter(Boolean)
      .join("\n"),
  } satisfies ChatContext;
  store.setChatContext(context);
  store.setChatSidebarOpen(true);
  store.setPendingChatNotice(
    `${snapshot.definition.label}: ${step.label}. ${step.instruction} Studio will retain ${step.proof}.`,
  );
  window.setTimeout(() => {
    window.dispatchEvent(new CustomEvent("cogo:focus-chat-input"));
  }, 0);
  return context;
}

export function detectStudioGoldenJourneyIntent(
  input: string,
  hasActiveJourney = Boolean(readSession()),
): StudioGoldenJourneyIntent | null {
  const normalized = input.trim().toLowerCase();
  if (!normalized) return null;
  if (
    /(change once|sync everything|semantic sync|한 번 바꾸|한번 바꾸|모두 동기화|전체 동기화)/i.test(
      normalized,
    )
  ) {
    return { action: "start", journeyId: "semantic-sync-publish" };
  }
  if (
    /(figma|피그마).*(journey|여정|처음|배포|publish)|(?:journey|여정).*(figma|피그마)/i.test(
      normalized,
    )
  ) {
    return { action: "start", journeyId: "figma-to-publish" };
  }
  if (
    /(document|docx|hwpx|문서).*(journey|여정|왕복|roundtrip|페이지별)|(?:journey|여정).*(document|문서)/i.test(
      normalized,
    )
  ) {
    return { action: "start", journeyId: "document-roundtrip" };
  }
  if (
    /(data|데이터).*(actionflow|action flow|액션플로우|여정|journey)|(?:journey|여정).*(data|데이터)/i.test(
      normalized,
    )
  ) {
    return { action: "start", journeyId: "data-actionflow" };
  }
  if (
    hasActiveJourney &&
    /^(continue|next|계속|다음|다음 단계|이어서)[.! ]*$/i.test(normalized)
  ) {
    return { action: "continue" };
  }
  if (
    hasActiveJourney &&
    /(journey|여정).*(status|progress|상태|진행)|(?:status|progress|상태|진행).*(journey|여정)/i.test(
      normalized,
    )
  ) {
    return { action: "inspect" };
  }
  return null;
}

function activeSnapshot(): StudioGoldenJourneySnapshot | null {
  return getActiveStudioGoldenJourneySnapshot({
    pending: listPendingStudioCapabilities(),
    executions: listStudioCapabilityExecutions(),
  });
}

export function registerStudioGoldenJourneyTools(bus: StudioMcpBus): void {
  bus.registerTool("start_golden_journey", {
    description:
      "Start a project-scoped Studio golden journey and focus its first chat-controlled operation.",
    metadata: { readOnly: true, tags: ["journey", "chat", "webmcp"] },
    inputSchema: {
      type: "object",
      properties: {
        journeyId: {
          type: "string",
          enum: Object.keys(STUDIO_GOLDEN_JOURNEYS),
        },
      },
      required: ["journeyId"],
      additionalProperties: false,
    },
    handler: (payload) => {
      if (!isJourneyId(payload.journeyId)) {
        throw new Error(
          `Unknown Studio golden journey: ${String(payload.journeyId)}`,
        );
      }
      startStudioGoldenJourney(payload.journeyId);
      const snapshot = activeSnapshot();
      if (!snapshot)
        throw new Error("The Studio golden journey could not be started.");
      activateStudioGoldenJourneyStep(snapshot);
      return snapshot;
    },
  });

  bus.registerTool("inspect_golden_journey", {
    description:
      "Inspect the active Studio golden journey, its completed steps, and the next required proof.",
    metadata: { readOnly: true, tags: ["journey", "chat", "webmcp"] },
    inputSchema: {
      type: "object",
      properties: {},
      additionalProperties: false,
    },
    handler: () => activeSnapshot(),
  });

  bus.registerTool("continue_golden_journey", {
    description:
      "Focus the next incomplete operation in the active Studio golden journey without mutating the project.",
    metadata: { readOnly: true, tags: ["journey", "chat", "webmcp"] },
    inputSchema: {
      type: "object",
      properties: {},
      additionalProperties: false,
    },
    handler: () => {
      const snapshot = activeSnapshot();
      if (!snapshot) throw new Error("No Studio golden journey is active.");
      activateStudioGoldenJourneyStep(snapshot);
      return snapshot;
    },
  });

  bus.registerTool("clear_golden_journey", {
    description:
      "Clear the project-scoped Studio golden journey guide without changing project content.",
    metadata: { readOnly: true, tags: ["journey", "chat", "webmcp"] },
    inputSchema: {
      type: "object",
      properties: {},
      additionalProperties: false,
    },
    handler: () => {
      clearStudioGoldenJourney();
      return { cleared: true, projectId: projectId() };
    },
  });
}
